/**
 * Progresso de mundo além de XP: elites derrotados, estado do gate, bosses derrotados
 * e Alpha Soul. Estruturado para vários bosses. Estado puro + helpers; o Game orquestra.
 */
import { GameConfig } from "../config/GameConfig";
import type { MissionsSnapshot } from "./Missions";
import type { AreaGoalsSnapshot } from "./AreaGoals";
import type { TerritoryProgressSnapshot } from "./TerritoryProgress";
import type { DailySnapshot } from "./DailyMission";
import type { MailSnapshot } from "./Mail";
import { emptySkills, loadSkills, loadTraining, type SkillsState, type TrainingState } from "./Skills";

export interface BossesDefeated {
  voidAlpha: boolean;
  aetherColossus: boolean; // Fase 9
}

export interface Discoveries {
  sanctuary: boolean;
  altar: boolean;
  alphaDen: boolean;
}

export interface ProgressSnapshot {
  elitesDefeated: number;
  alphaGateOpen: boolean;
  bossesDefeated: BossesDefeated;
  alphaSoul: number;
  discoveries: Discoveries;
  wardensDefeated: number; // Fase 9 — Shard Wardens abatidos (desbloqueio do Colossus)
  aetherCore: number; // Fase 9 — recurso único do Aether Colossus
  absorbHp: number; // 10.4O — +vida máxima PERMANENTE absorvida de monstros
  absorbPower: number; // 10.4O — +dano PERMANENTE absorvido de monstros
  coins: number; // 10.4Y — moeda do jogo (Fragmentos), gasta na Loja
  shopBought: Record<string, number>; // 10.4Y — nº de compras por item (escala o preço)
  outfit: string; // 10.4Z — roupa selecionada (cosmético)
  spellLoadout?: Record<string, string[]>; // 10.9FC
  outfitsOwned: string[]; // 10.4Z — roupas possuídas
  autoSellRarity: string; // 10.9M — auto-vender itens ATÉ esta raridade ("off" = desligado)
  classId?: string; // 10.9T — classe escolhida (warrior/archer/mage); "" = ainda não escolheu
  petOwned: boolean; // 10.5C — companheiro desbloqueado
  petLevel?: number; // 10.9L21
  petXp?: number; // 10.9L21
  voidPointsBought: number; // 10.5L — Pontos do Void comprados com Fragmentos
  treeResets?: number; // 10.9Y — nº de resets da Árvore Void (1º grátis, depois custa 💎)
  missions?: MissionsSnapshot; // 10.5O — missões ativas + quantas vezes cada uma foi cumprida
  tips?: string[]; // 10.5U — dicas de tutorial já mostradas
  /** 10.6H — PROFUNDIDADE: volta atual do mundo. 1 = primeira passagem; sobe sem fim. */
  depth?: number;
  /** 10.6H — progresso dos objetivos que destrancam o portão de cada área. */
  areaGoals?: AreaGoalsSnapshot;
  /** 10.6L — abates por território (o que abre as portas dos muros). */
  territories?: TerritoryProgressSnapshot;
  /** 10.6M — missão diária (dia, progresso, coletada, total). */
  daily?: DailySnapshot;
  /** 10.6P — kills do boss de área por região (escala a dificuldade). */
  areaBossKills?: Record<string, number>;
  /** 10.9CC — abates acumulados rumo ao próximo Guardião, por região. */
  guardianKills?: Record<string, number>;
  /** 10.6Q — limite diário de bosses fixos (respawn 1h, máx 6/dia). */
  bossDay?: string;
  bossKillsToday?: number;
  /** 10.6R — diamantes (moeda premium ganha jogando). */
  diamonds?: number;
  /** 10.9O — elixires de proteção da forja (impedem cair de nível ao falhar). */
  elixirs?: number;
  bagSlots?: number; // 10.9HA
  firstPackBought?: boolean; // 10.9J8
  autoFuseOwned?: boolean; // 10.9K5 — Fusão Automática comprada (💎)
  evolveMigrated?: boolean; // 10.9K9 — cópias da fusão antiga já viraram barra de evolução
  dailyCaps?: Record<string, { day: string; n: number }>; // 10.9K2 — tetos diários (ex.: 💎 do PvP)
  potions?: number; // 10.9CZ
  /** 10.6R — áreas VIP desbloqueadas. */
  vipAreasUnlocked?: string[];
  /** 10.6S — tier VIP (0=nenhum, 1=bronze … 6=mestre). */
  vipTier?: number;
  /** 10.9BG — pontos VIP acumulados (💎 gastos + comprados); o tier deriva deles. */
  vipPoints?: number;
  /** 10.6U — regen permanente comprada com 💎 (HP/s). */
  absorbRegen?: number;
  /** 10.6U — timestamp (ms) em que o Passe VIP expira (0 = sem passe). */
  passExpiresAt?: number;
  /** 10.8F — abates totais (ranking). */
  totalKills?: number;
  /** 10.9D — vitórias em duelo PvP (ranking). */
  pvpWins?: number;
  /** 10.9K0 — abates/vitórias DESTE personagem na última sincronização aceita da temporada. */
  seasonMark?: { season: number; kills: number; pvp: number } | null;
  /** 10.9H — derrotas em duelo PvP (registro W/L). */
  pvpLosses?: number;
  /** 10.9AC — timestamp (ms) do próximo evento Invasão do Vazio (relógio de 4h). */
  worldEventNextAt?: number;
  /** 10.9AD — Bênçãos (Bless): estoque que PROTEGE contra a perda de item ao morrer. */
  bless?: number;
  /** 10.9AF — nome do personagem (escolhido na criação; único entre os personagens). */
  charName?: string;
  /** 10.9FL — último token de brinde (compensação) já recebido por este personagem. */
  giftClaimed?: number;
  /** 10.9GA — Índice: defIds de equipamentos já descobertos. */
  discoveredItems?: string[];
  /** 10.9AJ — Zona dos Bosses: timestamp (ms) em que cada sala volta a ter boss (cooldown de farm). */
  bossZoneNextAt?: Record<string, number>;
  /** 10.9EO — RAIDS cuja recompensa ja foi resgatada (1x por horario). */
  raidClaimed?: string[];
  /** 10.9ES — ids das atualizacoes (Changelog) ja vistas/resgatadas. */
  updatesSeen?: string[];
  /** 10.9BE — anúncios recompensados: contadores diários por placement. */
  ads?: { day: string; used: Record<string, number> };
  /** 10.9BY — skills (7 perícias, nível 1→100 + xp). */
  skills?: Partial<SkillsState>;
  /** 10.9BY — treino offline em andamento (Boneco de Treino). */
  training?: TrainingState | null;
  /** 10.6R — correio (mensagens + recompensas). */
  mail?: { messages: MailSnapshot; lastDailyDay: string };
  /** 10.9L20 — até qual Profundidade o jogador já viu a explicação de "Nova Volta" no Mapa. */
  depthIntroSeen?: number;
  /** 10.9L19 — boosters da Loja de Diamantes: timestamp (ms) de expiração, não segundos restantes
   *  (senão o tempo comprado zerava ao sair e voltar do jogo). */
  xpBoostEndAt?: number;
  xpBoostMult?: number;
  coinBoostEndAt?: number;
  coinBoostMult?: number;
  dropBoostEndAt?: number;
}

export class GameProgress {
  elitesDefeated = 0;
  alphaGateOpen = false;
  bossesDefeated: BossesDefeated = { voidAlpha: false, aetherColossus: false };
  alphaSoul = 0;
  discoveries: Discoveries = { sanctuary: false, altar: false, alphaDen: false };
  wardensDefeated = 0;
  aetherCore = 0;
  absorbHp = 0; // +vida máxima permanente (absorção de monstros)
  absorbPower = 0; // +dano permanente (absorção de monstros)
  coins = 0; // 10.4Y — moeda do jogo (Fragmentos)
  shopBought: Record<string, number> = {}; // 10.4Y — compras por item (escala preço)
  outfit = "void"; // 10.4Z — roupa selecionada
  spellLoadout: Record<string, string[]> = {}; // 10.9FC — Grimorio: 3 magias escolhidas por classe
  outfitsOwned: string[] = ["void"]; // 10.4Z — roupas possuídas (void é grátis)
  autoSellRarity = "off"; // 10.9M — auto-venda por raridade ("off"|common|uncommon|rare|...)
  classId = ""; // 10.9T — classe escolhida ("" = escolher ao entrar)
  petOwned = false; // 10.5C — companheiro desbloqueado
  petLevel = 1; // 10.9L21 — nível do pet (sobe com XP de abate; amplia alcance + dá dano)
  petXp = 0; // 10.9L21 — XP acumulada rumo ao próximo nível do pet
  /** 10.9L22 — soma XP de abate ao pet; sobe de nível SEM TETO e devolve true se subiu. */
  addPetXp(n: number): boolean {
    this.petXp += Math.max(0, n);
    let leveled = false;
    while (this.petXp >= GameConfig.pet.xpForLevel(this.petLevel)) {
      this.petXp -= GameConfig.pet.xpForLevel(this.petLevel);
      this.petLevel += 1;
      leveled = true;
    }
    return leveled;
  }
  /** 10.9L21 — multiplicador de alcance de coleta de essência do pet neste nível. */
  petMagnetMult(): number {
    return GameConfig.pet.magnetMultBase + (this.petLevel - 1) * GameConfig.pet.magnetMultPerLevel;
  }
  /** 10.9L21 — bônus de dano do Caçador vindo do nível do pet (0 se não tiver pet). */
  petDamageMult(): number {
    return this.petOwned ? (this.petLevel - 1) * GameConfig.pet.dmgMultPerLevel : 0;
  }
  voidPointsBought = 0; // 10.5L — Pontos do Void comprados na Loja
  treeResets = 0; // 10.9Y — resets da Árvore Void feitos (custo escala; 1º grátis)
  missions: MissionsSnapshot | undefined = undefined; // 10.5O — estado das missões
  tips: string[] = []; // 10.5U — tutorial contextual já visto
  depth = 1; // 10.6H
  depthIntroSeen = 1; // 10.9L20 — até qual Profundidade já viu a explicação de "Nova Volta"
  areaGoals: AreaGoalsSnapshot | undefined = undefined; // 10.6H
  territories: TerritoryProgressSnapshot | undefined = undefined; // 10.6L
  daily: DailySnapshot | undefined = undefined; // 10.6M
  areaBossKills: Record<string, number> = {}; // 10.6P
  guardianKills: Record<string, number> = {}; // 10.9CC
  bossDay = ""; // 10.6Q — dia atual (YYYY-MM-DD) do limite diário
  bossKillsToday = 0; // 10.6Q — bosses fixos mortos hoje
  diamonds = 0; // 10.6R — moeda premium (ganha jogando)
  elixirs = 0; // 10.9O — elixires de proteção da forja
  bagSlots = 0; // 10.9HA — espaços extras da mochila comprados com 💎
  firstPackBought = false; // 10.9J8 — 1ª compra de pacote de 💎 já usada (dobra o pacote, 1x só)
  autoFuseOwned = false; // 10.9K5 — Fusão Automática comprada; 10.9K9: item saiu da loja — true = reembolsar 150💎
  evolveMigrated = false; // 10.9K9 — migração fusão → evolução feita neste personagem
  potions = 0; // 10.9CZ — poções de vida (uso manual)
  vipAreasUnlocked: string[] = []; // 10.6R — áreas VIP desbloqueadas
  vipTier = 0; // 10.6S — tier VIP (0=nenhum, 1=bronze, 2=prata, 3=ouro)
  absorbRegen = 0; // 10.6U — regen permanente (HP/s) comprada com 💎
  passExpiresAt = 0; // 10.6U — timestamp (ms) do fim do Passe VIP
  totalKills = 0; // 10.8F — abates totais (ranking)
  seasonMark: { season: number; kills: number; pvp: number } | null = null; // 10.9K0 — ver CloudSeason.syncSeason
  pvpWins = 0; // 10.9D — vitórias em duelo PvP (ranking)
  pvpLosses = 0; // 10.9H — derrotas em duelo PvP (registro W/L)
  worldEventNextAt = 0; // 10.9AC — timestamp (ms) do próximo evento de 4h (0 = ainda não agendado)
  bless = 0; // 10.9AD — bênçãos em estoque (cada morte consome 1 e evita perder item da bolsa)
  charName = ""; // 10.9AF — nome do personagem (escolhido ao criar)
  giftClaimed = 0; // 10.9FL — ultimo token de brinde ja recebido por este personagem
  discoveredItems: string[] = []; // 10.9GA — Indice: defIds de equipamentos ja vistos
  discoverItem(id: string): void { if (id && !this.discoveredItems.includes(id)) this.discoveredItems.push(id); } // 10.9GA
  bossZoneNextAt: Record<string, number> = {}; // 10.9AJ — cooldown por sala da Zona dos Bosses (epoch ms)
  raidClaimed: string[] = []; // 10.9EO — horarios de raid ja resgatados (guarda os ultimos 30)
  updatesSeen: string[] = []; // 10.9ES — novidades ja vistas (recompensa 1x por atualizacao)
  ads: { day: string; used: Record<string, number> } | undefined = undefined; // 10.9BE
  skills: SkillsState = emptySkills(); // 10.9BY — perícias (treino)
  training: TrainingState | null = null; // 10.9BY — treino offline (1 por vez)

  // 10.9L19 — boosters da Loja: guardados como TIMESTAMP de fim (não segundos restantes), senão
  // o tempo comprado zerava ao fechar/reabrir o jogo (o Game só tinha isso em memória).
  xpBoostEndAt = 0;
  xpBoostMult = 1;
  coinBoostEndAt = 0;
  coinBoostMult = 1;
  dropBoostEndAt = 0;
  /** Compra empilha: SOMA a duração ao tempo que ainda falta (Thiago: "comprar de novo não tava somando"). */
  addXpBoost(seconds: number, mult: number): void {
    const now = Date.now();
    this.xpBoostEndAt = Math.max(now, this.xpBoostEndAt) + Math.max(0, seconds) * 1000;
    this.xpBoostMult = mult;
  }
  /** Recompensa de anúncio: só ESTENDE até pelo menos X (não empilha anúncio repetido). */
  extendXpBoost(seconds: number, minMult: number): void {
    const now = Date.now();
    this.xpBoostEndAt = Math.max(this.xpBoostEndAt, now + seconds * 1000);
    this.xpBoostMult = Math.max(this.xpBoostMult, minMult);
  }
  xpBoostSecondsLeft(): number { return Math.max(0, this.xpBoostEndAt - Date.now()) / 1000; }
  addCoinBoost(seconds: number, mult: number): void {
    const now = Date.now();
    this.coinBoostEndAt = Math.max(now, this.coinBoostEndAt) + Math.max(0, seconds) * 1000;
    this.coinBoostMult = mult;
  }
  coinBoostSecondsLeft(): number { return Math.max(0, this.coinBoostEndAt - Date.now()) / 1000; }
  addDropBoost(seconds: number): void {
    const now = Date.now();
    this.dropBoostEndAt = Math.max(now, this.dropBoostEndAt) + Math.max(0, seconds) * 1000;
  }
  dropBoostSecondsLeft(): number { return Math.max(0, this.dropBoostEndAt - Date.now()) / 1000; }

  // 10.9AD — Bless
  addBless(n: number): void { this.bless += Math.max(0, Math.floor(n)); }
  /** Consome 1 bênção se houver; true = protegeu. */
  consumeBless(): boolean {
    if (this.bless <= 0) return false;
    this.bless -= 1;
    return true;
  }

  /** Absorve bônus permanentes de um monstro morto (§ recompensas de monstro). */
  absorb(hp: number, power: number): void {
    this.absorbHp += Math.max(0, hp);
    this.absorbPower += Math.max(0, power);
  }

  /** 10.4Y — moeda. */
  addCoins(n: number): void {
    this.coins += Math.max(0, Math.floor(n));
  }
  /** Gasta moedas; true se tinha saldo. */
  spendCoins(n: number): boolean {
    const cost = Math.max(0, Math.floor(n));
    if (this.coins < cost) return false;
    this.coins -= cost;
    return true;
  }
  shopCount(id: string): number {
    return this.shopBought[id] ?? 0;
  }
  recordShopBuy(id: string): void {
    this.shopBought[id] = this.shopCount(id) + 1;
  }

  /** 10.4Z — roupas. */
  ownsOutfit(id: string): boolean {
    return this.outfitsOwned.includes(id);
  }
  addOutfit(id: string): void {
    if (!this.ownsOutfit(id)) this.outfitsOwned.push(id);
  }
  selectOutfit(id: string): void {
    this.outfit = id;
  }

  /** Marca um POI como descoberto; retorna true se foi a PRIMEIRA vez. */
  discover(key: keyof Discoveries): boolean {
    if (this.discoveries[key]) return false;
    this.discoveries[key] = true;
    return true;
  }

  recordEliteKill(): number {
    this.elitesDefeated += 1;
    return this.elitesDefeated;
  }

  /** Deve abrir o gate agora? (elites suficientes e ainda fechado) */
  canOpenGate(): boolean {
    return !this.alphaGateOpen && this.elitesDefeated >= GameConfig.gate.requiredElites;
  }

  openGate(): void {
    this.alphaGateOpen = true;
  }

  /** Marca o boss como derrotado; retorna true se foi a PRIMEIRA vez. */
  recordBossDefeat(): boolean {
    const first = !this.bossesDefeated.voidAlpha;
    this.bossesDefeated.voidAlpha = true;
    return first;
  }

  grantAlphaSoul(): void {
    this.alphaSoul += 1;
  }

  // ---- Fase 9 ----
  recordWardenKill(): number {
    this.wardensDefeated += 1;
    return this.wardensDefeated;
  }
  /** Marca o Aether Colossus como derrotado; true na PRIMEIRA vez. */
  recordColossusDefeat(): boolean {
    const first = !this.bossesDefeated.aetherColossus;
    this.bossesDefeated.aetherColossus = true;
    return first;
  }
  grantAetherCore(): void {
    this.aetherCore += 1;
  }
  get region2Completed(): boolean {
    return this.bossesDefeated.aetherColossus;
  }

  // 10.6Q — limite diário de bosses fixos
  private todayKey(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  canSpawnFixedBoss(): boolean {
    const today = this.todayKey();
    if (this.bossDay !== today) { this.bossDay = today; this.bossKillsToday = 0; }
    return this.bossKillsToday < 6;
  }
  recordFixedBossKill(): number {
    const today = this.todayKey();
    if (this.bossDay !== today) { this.bossDay = today; this.bossKillsToday = 0; }
    this.bossKillsToday += 1;
    return this.bossKillsToday;
  }
  get dailyBossesRemaining(): number {
    const today = this.todayKey();
    if (this.bossDay !== today) return 6;
    return Math.max(0, 6 - this.bossKillsToday);
  }

  // 10.6R — diamantes
  addDiamonds(n: number): void { this.diamonds += Math.max(0, Math.floor(n)); }
  spendDiamonds(n: number): boolean {
    const cost = Math.max(0, Math.floor(n));
    if (this.diamonds < cost) return false;
    this.diamonds -= cost;
    this.addVipPoints(cost); // 10.9BG — todo 💎 gasto vira ponto VIP
    return true;
  }

  /**
   * 10.9K2 — TETO DIÁRIO por chave (ex.: "pvpDia"): devolve quanto de `amount` ainda cabe hoje
   * (dia local do aparelho) e já desconta. Zera sozinho quando o dia muda.
   */
  dailyCaps: Record<string, { day: string; n: number }> = {};
  takeDailyCapped(key: string, amount: number, cap: number): number {
    const d = new Date();
    const today = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    const cur = this.dailyCaps[key];
    const used = cur && cur.day === today ? cur.n : 0;
    const take = Math.max(0, Math.min(Math.floor(amount), cap - used));
    this.dailyCaps[key] = { day: today, n: used + take };
    return take;
  }

  // 10.9BG — PONTOS VIP (acumulados, nunca descem). 10.9K2: só 💎 GASTOS (pago ou grátis, 1 ponto
  // cada) — comprar não soma mais (contava 2× o 💎 comprado: na compra e de novo no gasto).
  vipPoints = 0;
  onVipPoints: (() => void) | null = null;
  addVipPoints(n: number): void {
    const add = Math.max(0, Math.floor(n));
    if (add <= 0) return;
    this.vipPoints += add;
    this.onVipPoints?.();
  }
  /** Tier que os pontos atuais dão direito (0 = nenhum). */
  vipTierFromPoints(): number {
    const tiers = GameConfig.vipTiers;
    let t = 0;
    for (let i = 0; i < tiers.length; i++) if (this.vipPoints >= tiers[i].cost) t = i + 1;
    return t;
  }

  // 10.9O — elixires de proteção da forja
  addElixirs(n: number): void { this.elixirs += Math.max(0, Math.floor(n)); }
  addPotions(n: number): void { this.potions += Math.max(0, Math.floor(n)); } // 10.9CZ
  spendPotion(): boolean { if (this.potions <= 0) return false; this.potions -= 1; return true; } // 10.9CZ
  spendElixir(): boolean {
    if (this.elixirs <= 0) return false;
    this.elixirs -= 1;
    return true;
  }

  // 10.6R — áreas VIP
  isVipAreaUnlocked(id: string): boolean { return this.vipAreasUnlocked.includes(id); }
  unlockVipArea(id: string): boolean {
    if (this.isVipAreaUnlocked(id)) return false;
    this.vipAreasUnlocked.push(id);
    return true;
  }

  // 10.6P — boss de área
  areaBossKillCount(regionId: string): number {
    return this.areaBossKills[regionId] ?? 0;
  }
  recordAreaBossKill(regionId: string): number {
    const n = this.areaBossKillCount(regionId) + 1;
    this.areaBossKills[regionId] = n;
    return n;
  }

  snapshot(): ProgressSnapshot {
    return {
      elitesDefeated: this.elitesDefeated,
      depth: this.depth,
      depthIntroSeen: this.depthIntroSeen, // 10.9L20
      areaGoals: this.areaGoals,
      territories: this.territories,
      daily: this.daily,
      areaBossKills: { ...this.areaBossKills },
      guardianKills: { ...this.guardianKills }, // 10.9CC
      bossDay: this.bossDay,
      bossKillsToday: this.bossKillsToday,
      alphaGateOpen: this.alphaGateOpen,
      bossesDefeated: { voidAlpha: this.bossesDefeated.voidAlpha, aetherColossus: this.bossesDefeated.aetherColossus },
      alphaSoul: this.alphaSoul,
      discoveries: { ...this.discoveries },
      wardensDefeated: this.wardensDefeated,
      aetherCore: this.aetherCore,
      absorbHp: this.absorbHp,
      absorbPower: this.absorbPower,
      coins: this.coins,
      shopBought: { ...this.shopBought },
      outfit: this.outfit,
      spellLoadout: this.spellLoadout, // 10.9FC
      outfitsOwned: [...this.outfitsOwned],
      autoSellRarity: this.autoSellRarity,
      classId: this.classId,
      petOwned: this.petOwned,
      petLevel: this.petLevel, // 10.9L21
      petXp: this.petXp,
      voidPointsBought: this.voidPointsBought,
      treeResets: this.treeResets,
      missions: this.missions,
      tips: [...this.tips],
      diamonds: this.diamonds,
      elixirs: this.elixirs,
      bagSlots: this.bagSlots,
      firstPackBought: this.firstPackBought, // 10.9J8
      autoFuseOwned: this.autoFuseOwned, // 10.9K5
      evolveMigrated: this.evolveMigrated, // 10.9K9
      dailyCaps: { ...this.dailyCaps }, // 10.9K2
      potions: this.potions, // 10.9CZ
      vipAreasUnlocked: [...this.vipAreasUnlocked],
      vipTier: this.vipTier,
      vipPoints: this.vipPoints, // 10.9BG
      absorbRegen: this.absorbRegen,
      passExpiresAt: this.passExpiresAt,
      totalKills: this.totalKills,
      seasonMark: this.seasonMark ? { ...this.seasonMark } : null, // 10.9K0
      pvpWins: this.pvpWins,
      pvpLosses: this.pvpLosses,
      worldEventNextAt: this.worldEventNextAt,
      bless: this.bless,
      charName: this.charName,
      giftClaimed: this.giftClaimed, // 10.9FL
      discoveredItems: this.discoveredItems, // 10.9GA
      bossZoneNextAt: { ...this.bossZoneNextAt },
      raidClaimed: this.raidClaimed.slice(-30),
      // 10.9J1 — Thiago: "as recompensas de update estao chegando mesmo depois... outra estao dando
      // recompensa demais". Era ISTO: o Changelog ja passa de 80 entradas e só cresce, mas o save
      // só guardava as ÚLTIMAS 60 vistas. Assim que um jogador ativo cruzava esse teto, o `.slice`
      // derrubava os ids MAIS ANTIGOS da lista — e `unseenUpdates` (Changelog.ts) considera "não
      // visto" tudo que não está na lista. As atualizações antigas "esquecidas" voltavam a contar
      // como novas e pagavam a recompensa DE NOVO, se empilhando com o resto — daí "demais". Sem
      // teto: são strings curtas, uma por atualização publicada (dezenas por ano, não milhares),
      // desprezível no tamanho do save perto do resto (correio, itens, missões).
      updatesSeen: this.updatesSeen,
      ads: this.ads ? { day: this.ads.day, used: { ...this.ads.used } } : undefined, // 10.9BE
      skills: JSON.parse(JSON.stringify(this.skills)) as SkillsState, // 10.9BY
      training: this.training ? { ...this.training } : null, // 10.9BY
      xpBoostEndAt: this.xpBoostEndAt, // 10.9L19
      xpBoostMult: this.xpBoostMult,
      coinBoostEndAt: this.coinBoostEndAt,
      coinBoostMult: this.coinBoostMult,
      dropBoostEndAt: this.dropBoostEndAt,
    };
  }

  load(data: Partial<ProgressSnapshot> | undefined): void {
    const d = data ?? {};
    this.elitesDefeated = Math.max(0, Math.floor(d.elitesDefeated ?? 0));
    this.alphaGateOpen = d.alphaGateOpen === true;
    this.bossesDefeated = { voidAlpha: d.bossesDefeated?.voidAlpha === true, aetherColossus: d.bossesDefeated?.aetherColossus === true };
    this.alphaSoul = Math.max(0, Math.floor(d.alphaSoul ?? 0));
    const disc: Partial<Discoveries> = d.discoveries ?? {};
    this.discoveries = {
      sanctuary: disc.sanctuary === true,
      altar: disc.altar === true,
      alphaDen: disc.alphaDen === true,
    };
    this.wardensDefeated = Math.max(0, Math.floor(d.wardensDefeated ?? 0));
    this.depth = Math.min(GameConfig.depth.maxDepth, Math.max(1, Math.floor(d.depth ?? 1))); // 10.6H
    // 10.9L20 — save antigo sem o campo: assume que já viu (não mostra a explicação retroativamente
    // pra quem já está em volta avançada); jogador novo cai em depth=1 e nunca dispara o popup.
    this.depthIntroSeen = Math.max(1, Math.floor(d.depthIntroSeen ?? this.depth));
    this.areaGoals = d.areaGoals; // 10.6H
    this.territories = d.territories; // 10.6L
    this.daily = d.daily; // 10.6M
    this.aetherCore = Math.max(0, Math.floor(d.aetherCore ?? 0));
    this.absorbHp = Math.max(0, Math.floor(d.absorbHp ?? 0));
    this.absorbPower = Math.max(0, Math.floor(d.absorbPower ?? 0));
    this.coins = Math.max(0, Math.floor(d.coins ?? 0));
    this.shopBought = {};
    if (d.shopBought && typeof d.shopBought === "object") {
      for (const [k, v] of Object.entries(d.shopBought)) {
        if (typeof v === "number" && Number.isFinite(v)) this.shopBought[k] = Math.max(0, Math.floor(v));
      }
    }
    this.outfitsOwned = ["void"];
    if (Array.isArray(d.outfitsOwned)) for (const id of d.outfitsOwned) if (typeof id === "string" && !this.outfitsOwned.includes(id)) this.outfitsOwned.push(id);
    this.outfit = typeof d.outfit === "string" && this.outfitsOwned.includes(d.outfit) ? d.outfit : "void";
    this.autoSellRarity = typeof d.autoSellRarity === "string" ? d.autoSellRarity : "off"; // 10.9M
    this.spellLoadout = {}; // 10.9FC — campo novo, sem bumpar a versao do save
    if (d.spellLoadout && typeof d.spellLoadout === "object") {
      for (const [k, v] of Object.entries(d.spellLoadout as Record<string, unknown>)) {
        if (Array.isArray(v)) this.spellLoadout[k] = v.filter((x): x is string => typeof x === "string").slice(0, 3);
      }
    }
    this.classId = typeof d.classId === "string" ? d.classId : ""; // 10.9T
    this.petOwned = d.petOwned === true;
    this.petLevel = Math.max(1, Math.floor(d.petLevel ?? 1)); // 10.9L22 — sem teto
    this.petXp = Math.max(0, Math.floor(d.petXp ?? 0));
    // 10.5L — campo novo: saves antigos caem no default 0 (sem bumpar a versão do save)
    this.voidPointsBought = Math.max(0, Math.floor(d.voidPointsBought ?? 0));
    this.treeResets = Math.max(0, Math.floor(d.treeResets ?? 0)); // 10.9Y
    this.missions = d.missions && typeof d.missions === "object" ? d.missions : undefined; // 10.5O
    this.tips = Array.isArray(d.tips) ? d.tips.filter((t) => typeof t === "string") : []; // 10.5U
    // 10.6P — boss de área kills
    this.guardianKills = {}; // 10.9CC
    if (d.guardianKills && typeof d.guardianKills === "object") {
      for (const [k, v] of Object.entries(d.guardianKills)) {
        if (typeof v === "number" && Number.isFinite(v)) this.guardianKills[k] = Math.max(0, Math.floor(v));
      }
    }
    this.areaBossKills = {};
    if (d.areaBossKills && typeof d.areaBossKills === "object") {
      for (const [k, v] of Object.entries(d.areaBossKills)) {
        if (typeof v === "number" && Number.isFinite(v)) this.areaBossKills[k] = Math.max(0, Math.floor(v));
      }
    }
    // 10.6Q — limite diário de bosses fixos
    this.bossDay = typeof d.bossDay === "string" ? d.bossDay : "";
    this.bossKillsToday = Math.max(0, Math.floor(d.bossKillsToday ?? 0));
    // 10.6R — diamantes + VIP
    this.diamonds = Math.max(0, Math.floor(d.diamonds ?? 0));
    this.elixirs = Math.max(0, Math.floor(d.elixirs ?? 0)); // 10.9O
    this.bagSlots = Math.max(0, Math.min(100, Math.floor(d.bagSlots ?? 0))); // 10.9HA
    this.firstPackBought = d.firstPackBought === true; // 10.9J8
    this.autoFuseOwned = d.autoFuseOwned === true; // 10.9K5
    this.evolveMigrated = d.evolveMigrated === true; // 10.9K9
    this.dailyCaps = {}; // 10.9K2
    if (d.dailyCaps && typeof d.dailyCaps === "object") {
      for (const [k, v] of Object.entries(d.dailyCaps)) {
        if (v && typeof v.day === "string" && Number.isFinite(v.n)) this.dailyCaps[k] = { day: v.day, n: Math.max(0, Math.floor(v.n)) };
      }
    }
    this.potions = Math.max(0, Math.floor((d as { potions?: number }).potions ?? 0)); // 10.9CZ
    this.vipAreasUnlocked = Array.isArray(d.vipAreasUnlocked)
      ? d.vipAreasUnlocked.filter(v => typeof v === "string")
      : [];
    this.vipTier = Math.max(0, Math.min(GameConfig.vipTiers.length, Math.floor(d.vipTier ?? 0)));
    // 10.9BG — pontos VIP; save antigo (tier comprado, sem pontos): semeia os pontos do tier que já tinha
    this.vipPoints = Math.max(0, Math.floor(d.vipPoints ?? 0));
    if (this.vipTier > 0) this.vipPoints = Math.max(this.vipPoints, GameConfig.vipTiers[this.vipTier - 1].cost);
    this.absorbRegen = Math.max(0, Number(d.absorbRegen ?? 0));
    this.passExpiresAt = Math.max(0, Math.floor(d.passExpiresAt ?? 0));
    this.totalKills = Math.max(0, Math.floor(d.totalKills ?? 0)); // 10.8F
    // 10.9K0 — marca da temporada; save antigo não tem (null = o próximo sync marca o ponto de partida)
    const sm = d.seasonMark;
    this.seasonMark = sm && typeof sm === "object" && Number.isFinite(sm.season) && Number.isFinite(sm.kills) && Number.isFinite(sm.pvp)
      ? { season: Math.floor(sm.season), kills: Math.max(0, Math.floor(sm.kills)), pvp: Math.max(0, Math.floor(sm.pvp)) }
      : null;
    this.pvpWins = Math.max(0, Math.floor(d.pvpWins ?? 0)); // 10.9D
    this.pvpLosses = Math.max(0, Math.floor(d.pvpLosses ?? 0)); // 10.9H
    this.worldEventNextAt = Math.max(0, Math.floor(d.worldEventNextAt ?? 0)); // 10.9AC
    this.bless = Math.max(0, Math.floor(d.bless ?? 0)); // 10.9AD
    this.charName = typeof d.charName === "string" ? d.charName : ""; // 10.9AF
    this.giftClaimed = Math.max(0, Math.floor(Number((d as { giftClaimed?: unknown }).giftClaimed) || 0)); // 10.9FL
    this.discoveredItems = Array.isArray((d as { discoveredItems?: unknown }).discoveredItems) ? ((d as { discoveredItems: unknown[] }).discoveredItems).filter((x): x is string => typeof x === "string") : []; // 10.9GA
    // 10.9AJ — cooldowns da Zona dos Bosses (só números válidos)
    this.bossZoneNextAt = {};
    if (d.bossZoneNextAt && typeof d.bossZoneNextAt === "object") {
      for (const [k, v] of Object.entries(d.bossZoneNextAt)) {
        if (typeof v === "number" && Number.isFinite(v)) this.bossZoneNextAt[k] = Math.max(0, Math.floor(v));
      }
    }
    this.raidClaimed = Array.isArray(d.raidClaimed) ? d.raidClaimed.filter((x): x is string => typeof x === "string").slice(-30) : []; // 10.9EO
    this.updatesSeen = Array.isArray(d.updatesSeen) ? d.updatesSeen.filter((x): x is string => typeof x === "string") : []; // 10.9ES — 10.9J1: sem teto (ver o comentário no snapshot)
    this.ads = d.ads && typeof d.ads === "object" && typeof d.ads.day === "string" ? { day: d.ads.day, used: { ...(d.ads.used ?? {}) } } : undefined; // 10.9BE
    this.skills = loadSkills(d.skills); // 10.9BY — save antigo: tudo Nv1
    this.training = loadTraining(d.training); // 10.9BY
    // 10.9L19 — boosters: timestamp de expiração (já no passado = booster expirado, vira 0 no getter)
    this.xpBoostEndAt = Math.max(0, Math.floor(d.xpBoostEndAt ?? 0));
    this.xpBoostMult = Math.max(1, Number(d.xpBoostMult ?? 1));
    this.coinBoostEndAt = Math.max(0, Math.floor(d.coinBoostEndAt ?? 0));
    this.coinBoostMult = Math.max(1, Number(d.coinBoostMult ?? 1));
    this.dropBoostEndAt = Math.max(0, Math.floor(d.dropBoostEndAt ?? 0));
  }

  reset(): void {
    this.elitesDefeated = 0;
    this.alphaGateOpen = false;
    this.bossesDefeated = { voidAlpha: false, aetherColossus: false };
    this.alphaSoul = 0;
    this.discoveries = { sanctuary: false, altar: false, alphaDen: false };
    this.wardensDefeated = 0;
    this.aetherCore = 0;
    this.absorbHp = 0;
    this.absorbPower = 0;
    this.bagSlots = 0; // 10.9HA
    this.firstPackBought = false; // 10.9J8
    this.autoFuseOwned = false; // 10.9K5
    this.evolveMigrated = false; // 10.9K9
    this.dailyCaps = {}; // 10.9K2
    this.coins = 0;
    this.shopBought = {};
    this.outfit = "void";
    this.outfitsOwned = ["void"];
    this.autoSellRarity = "off"; // 10.9M
    this.classId = ""; // 10.9T — wipe total volta a pedir a classe
    this.petOwned = false;
    this.petLevel = 1; // 10.9L21
    this.petXp = 0;
    this.voidPointsBought = 0;
    this.treeResets = 0; // 10.9Y
    this.missions = undefined;
    this.tips = [];
    this.areaBossKills = {};
    this.guardianKills = {}; // 10.9CC
    this.bossDay = "";
    this.bossKillsToday = 0;
    this.diamonds = 0;
    this.elixirs = 0; // 10.9O
    this.vipAreasUnlocked = [];
    this.vipTier = 0;
    this.vipPoints = 0; // 10.9BG
    this.spellLoadout = {}; // 10.9FC
    this.absorbRegen = 0;
    this.passExpiresAt = 0;
    this.totalKills = 0;
    this.seasonMark = null; // 10.9K0
    this.pvpWins = 0;
    this.pvpLosses = 0;
    this.worldEventNextAt = 0; // 10.9AC
    this.bless = 0; // 10.9AD
    this.charName = ""; // 10.9AF
    this.giftClaimed = 0; // 10.9FL
    this.discoveredItems = []; // 10.9GA
    this.bossZoneNextAt = {}; // 10.9AJ
    this.raidClaimed = []; // 10.9EO
    this.updatesSeen = []; // 10.9ES
    this.skills = emptySkills(); // 10.9BY
    this.training = null; // 10.9BY
    this.xpBoostEndAt = 0; // 10.9L19
    this.xpBoostMult = 1;
    this.coinBoostEndAt = 0;
    this.coinBoostMult = 1;
    this.dropBoostEndAt = 0;
    this.depthIntroSeen = 1; // 10.9L20
  }
}
